/**
 * OPT-15（23 号 §7.3 / 25 号 §5）：分块增量 SHA-256。
 *
 * 为什么不用 `file.arrayBuffer()` + `crypto.subtle.digest`：
 * 那会把整个文件（2GB 视频）一次性读进内存，主线程有内存与卡顿风险。
 * 这里用纯 JS 增量实现，按 `chunkSize`（默认 4MB）顺序读取 Blob 分片，
 * 内存上限为「一个分片 + 512 字节状态」，并支持进度回调与取消。
 *
 * 关键约束：输出必须与 WebCrypto / 服务端 `sha256` 完全一致（同一算法，
 * 不做分块摘要拼接）。正确性由 `src/test/sha256-chunked.test.ts` 与
 * WebCrypto 逐字节比对保证。
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotr(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

/** 增量 SHA-256 状态机（每次 update 只保留 64 字节缓冲） */
export class Sha256 {
  private readonly h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);

  private readonly buffer = new Uint8Array(64);
  private bufferLength = 0;
  private bytesHashed = 0;
  private finished = false;

  update(data: Uint8Array): void {
    if (this.finished) throw new Error('SHA-256 已结束，不能继续 update');
    let offset = 0;
    this.bytesHashed += data.length;

    if (this.bufferLength > 0) {
      const need = 64 - this.bufferLength;
      const take = Math.min(need, data.length);
      this.buffer.set(data.subarray(0, take), this.bufferLength);
      this.bufferLength += take;
      offset = take;
      if (this.bufferLength === 64) {
        this.compress(this.buffer, 0);
        this.bufferLength = 0;
      }
    }

    while (offset + 64 <= data.length) {
      this.compress(data, offset);
      offset += 64;
    }

    if (offset < data.length) {
      this.buffer.set(data.subarray(offset), 0);
      this.bufferLength = data.length - offset;
    }
  }

  digest(): Uint8Array {
    if (!this.finished) {
      this.finished = true;
      const bitLengthHi = Math.floor(this.bytesHashed / 0x20000000);
      const bitLengthLo = (this.bytesHashed << 3) >>> 0;

      const padding = new Uint8Array(this.bufferLength < 56 ? 64 : 128);
      padding.set(this.buffer.subarray(0, this.bufferLength), 0);
      padding[this.bufferLength] = 0x80;
      const view = new DataView(padding.buffer);
      view.setUint32(padding.length - 8, bitLengthHi, false);
      view.setUint32(padding.length - 4, bitLengthLo, false);
      for (let offset = 0; offset < padding.length; offset += 64) {
        this.compress(padding, offset);
      }
    }

    const out = new Uint8Array(32);
    const outView = new DataView(out.buffer);
    for (let index = 0; index < 8; index += 1) {
      outView.setUint32(index * 4, this.h[index]!, false);
    }
    return out;
  }

  private compress(chunk: Uint8Array, offset: number): void {
    const w = new Uint32Array(64);
    const view = new DataView(chunk.buffer, chunk.byteOffset + offset, 64);
    for (let index = 0; index < 16; index += 1) {
      w[index] = view.getUint32(index * 4, false);
    }
    for (let index = 16; index < 64; index += 1) {
      const s0 = rotr(w[index - 15]!, 7) ^ rotr(w[index - 15]!, 18) ^ (w[index - 15]! >>> 3);
      const s1 = rotr(w[index - 2]!, 17) ^ rotr(w[index - 2]!, 19) ^ (w[index - 2]! >>> 10);
      w[index] = (w[index - 16]! + s0 + w[index - 7]! + s1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = this.h as unknown as number[];
    for (let index = 0; index < 64; index += 1) {
      const s1 = rotr(e!, 6) ^ rotr(e!, 11) ^ rotr(e!, 25);
      const ch = (e! & f!) ^ (~e! & g!);
      const temp1 = (h! + s1 + ch + K[index]! + w[index]!) >>> 0;
      const s0 = rotr(a!, 2) ^ rotr(a!, 13) ^ rotr(a!, 22);
      const maj = (a! & b!) ^ (a! & c!) ^ (b! & c!);
      const temp2 = (s0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d! + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    this.h[0] = (this.h[0]! + a!) >>> 0;
    this.h[1] = (this.h[1]! + b!) >>> 0;
    this.h[2] = (this.h[2]! + c!) >>> 0;
    this.h[3] = (this.h[3]! + d!) >>> 0;
    this.h[4] = (this.h[4]! + e!) >>> 0;
    this.h[5] = (this.h[5]! + f!) >>> 0;
    this.h[6] = (this.h[6]! + g!) >>> 0;
    this.h[7] = (this.h[7]! + h!) >>> 0;
  }
}

export interface ChunkedHashOptions {
  /** 分片大小（默认 4MB）；2GB 文件最多占用约 4MB 缓冲 */
  chunkSize?: number;
  /** 已处理字节数 / 总字节数（0–100，整数或小数） */
  onProgress?: (processedBytes: number, totalBytes: number) => void;
  /** 取消：读取下一个分片前检查，取消时抛出 AbortError */
  signal?: AbortSignal;
}

export function toHex(digest: Uint8Array): string {
  let hex = '';
  for (const byte of digest) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}

export class AbortError extends Error {
  constructor() {
    super('上传已取消');
    this.name = 'AbortError';
  }
}

/**
 * 对 Blob/File 做分块增量 SHA-256（不整文件读入内存）。
 * 结果与 `crypto.subtle.digest('SHA-256', bytes)` 完全一致。
 */
export async function sha256HexOfBlob(
  blob: Blob,
  options: ChunkedHashOptions = {},
): Promise<string> {
  const chunkSize = Math.max(64 * 1024, options.chunkSize ?? 4 * 1024 * 1024);
  const hash = new Sha256();
  let processed = 0;

  if (options.signal?.aborted) throw new AbortError();
  if (blob.size === 0) {
    options.onProgress?.(0, 0);
    return toHex(hash.digest());
  }

  while (processed < blob.size) {
    if (options.signal?.aborted) throw new AbortError();
    const end = Math.min(processed + chunkSize, blob.size);
    const buffer = await blob.slice(processed, end).arrayBuffer();
    if (options.signal?.aborted) throw new AbortError();
    hash.update(new Uint8Array(buffer));
    processed = end;
    options.onProgress?.(processed, blob.size);
  }
  return toHex(hash.digest());
}
